import { DEFAULT_LOCALE, extend as extendLocale } from '../helpers/i18n';
import { allSettled, fetchJSON, handleError } from './helpers';

import moment from 'moment';
import supportedLocales from '../helpers/i18nSupportedLocales.json';

const importLocalePhrases = locale =>
    fetchJSON(`/assets/locale/${locale}.json`).then(json => {
        extendLocale(json, locale);
        return {
            code: locale,
            locale: { ...json },
        };
    });

// One static import specifier per locale so the bundler can analyze and include
// each moment locale. A fully-dynamic `import(`moment/locale/${locale}`)` cannot
// be statically analyzed and would fail to resolve at runtime in the build.
const momentLocaleLoaders = {
    ar: () => import('moment/locale/ar'),
    de: () => import('moment/locale/de'),
    es: () => import('moment/locale/es'),
    fr: () => import('moment/locale/fr'),
};

// Derive the active set from the same JSON that drives translations so the two
// can't silently drift (webpack's ContextReplacementPlugin used to guarantee
// this). 'en' is built into moment and needs no loader. A supported locale with
// no entry above resolves to `undefined` here and is surfaced at load time.
const dateLocaleLoaders = Object.fromEntries(
    supportedLocales.filter(code => code !== 'en').map(code => [code, momentLocaleLoaders[code]])
);

const importLocalizedDateModule = locale => {
    // 'en' module is bundled with moment
    if (locale === 'en') {
        moment.locale(locale);
        return Promise.resolve();
    }

    const loadDateLocale = dateLocaleLoaders[locale];

    if (!loadDateLocale) {
        // No loader for this locale (the map and i18nSupportedLocales.json have
        // drifted, or an unsupported code slipped through). Surface it instead of
        // leaving dates silently in the previously loaded language, and still set
        // the locale so moment applies its own fallback.
        handleError(new Error(`No moment date-locale loader for "${locale}"`));
        moment.locale(locale);
        return Promise.resolve();
    }

    return loadDateLocale()
        .catch(handleError)
        .then(() => moment.locale(locale));
};

export const fetchLocale = ({ locale = DEFAULT_LOCALE } = {}) =>
    allSettled([importLocalizedDateModule(locale), importLocalePhrases(locale)]).then(
        ([, { state, value }]) => {
            return state === 'fulfilled' ? value : undefined;
        }
    );
